import {
  type IAdminWarehouseProductDetail,
  type ILeadActivity,
  type ILeadBasketProducts,
  type ILeadSessions,
} from '@mahawi/eshop-common/src/types';
import {
  EEventCode,
  EWarehouseAction,
  type IAdminEvent,
  type ICarrierCreatePacketResponseMessage,
  type IGeneralEvent,
  type IMessenger,
  type IProductEvent,
  type TWarehouseSyncResponses,
} from '@mahawi/eshop-rabbit/message';
import { Store, type UnknownAction } from 'redux';
import { Socket as SocketIO } from 'socket.io-client';

import {
  EMessengerSocketEvent,
  ENotificationSeverity,
  ESocketActions,
  ESocketRoom,
  getMessengerLeadRoom,
  ShowNotificationCallback,
  type ShowNotificationMessage,
} from '../admin-types';
import { OpenNotificationType } from './notification';
import { type RootState } from './reducers';
import { categoriesLoadAll } from './reducers/categories/reducer';
import { countriesLoadAll } from './reducers/countries/reducer';
import { featureFlagsLoadAll } from './reducers/feature-flags/reducer';
import {
  customerSendMessage,
  customerTick,
  leadActivity,
  leadBasketProducts,
  leadSessions,
} from './reducers/messenger/reducer';
import {
  ordersLoadAllOrderStatusTypeCodes,
  ordersLoadAllReviewStatusTypeCodes,
} from './reducers/orders/reducer';
import { propertiesLoadAllTypes } from './reducers/properties/reducer';
import { sendRaw } from './reducers/send-request';
import { tagsLoadAll } from './reducers/tags/reducer';
import { warehouseOrdersMarkStale } from './reducers/warehouse-order/reducer';
import { eventGeneral } from './socket/event-general';
import { eventProduct } from './socket/event-product';

export default class Socket {
  private openNotification: OpenNotificationType | undefined;

  private joinedRooms: Set<string> = new Set();

  public constructor(
    private readonly socket: SocketIO,
    private readonly store: Store<RootState>,
  ) {}

  public isConnected(): boolean {
    return this.socket.connected;
  }

  public setOpenNotification(openNotification: OpenNotificationType): void {
    this.openNotification = openNotification;
  }

  public async requests(): Promise<void> {
    const requestInterval: NodeJS.Timeout = setInterval((): void => {
      if (this.socket.connected) {
        clearInterval(requestInterval);

        sendRaw(this.socket, ordersLoadAllOrderStatusTypeCodes()).then(
          (response): void => {
            this.store.dispatch(response as UnknownAction);
          },
        );

        sendRaw(this.socket, ordersLoadAllReviewStatusTypeCodes()).then(
          (response): void => {
            this.store.dispatch(response as UnknownAction);
          },
        );

        sendRaw(this.socket, tagsLoadAll()).then((response): void => {
          this.store.dispatch(response as UnknownAction);
        });

        sendRaw(this.socket, propertiesLoadAllTypes()).then(
          (response): void => {
            this.store.dispatch(response as UnknownAction);
          },
        );

        sendRaw(this.socket, featureFlagsLoadAll()).then((response): void => {
          this.store.dispatch(response as UnknownAction);
        });

        sendRaw(this.socket, categoriesLoadAll()).then((response): void => {
          this.store.dispatch(response as UnknownAction);
        });

        sendRaw(this.socket, countriesLoadAll()).then((response): void => {
          this.store.dispatch(response as UnknownAction);
        });
      }
    }, 500);
  }

  public async subscribe(): Promise<void> {
    this.socket.on(
      ESocketActions.SHOW_NOTIFICATION,
      (message: ShowNotificationMessage): void => {
        this.store.dispatch({
          type: 'showNotification',
          ...message,
        });
      },
    );

    this.socket.on(
      ESocketActions.EVENT_GENERAL,
      (message: { payload: IGeneralEvent }): void =>
        eventGeneral({
          message: message.payload,
          showNotification: this.showNotification.bind(this),
        }),
    );

    this.socket.on(
      'warehouse.sync.response',
      (message: TWarehouseSyncResponses): void => {
        if (message.type === EWarehouseAction.FETCH_ORDERS) {
          this.store.dispatch(warehouseOrdersMarkStale());
          this.showNotification({
            message: message.success
              ? {
                  severity: ENotificationSeverity.INFO,
                  title: 'Warehouse orders fetched',
                  description: `${message.warehouse.name}: orders changed since ${message.changedSince} are being saved.`,
                }
              : {
                  severity: ENotificationSeverity.ERROR,
                  title: 'Warehouse orders fetch failed',
                  description: `${message.warehouse.name} — ${message.error ?? 'unknown error'}`,
                },
          });

          return;
        }

        if (message.type === EWarehouseAction.PLACE_ORDER) {
          this.showNotification({
            message: message.success
              ? {
                  severity: ENotificationSeverity.SUCCESS,
                  title: 'Order placed in warehouse',
                  description: `Order ${message.orderUUID}: placed in ${message.warehouse.name}${
                    message.externalOrderId
                      ? ` (external order ${message.externalOrderId})`
                      : ''
                  }.`,
                }
              : {
                  severity: ENotificationSeverity.ERROR,
                  title: 'Order placement failed',
                  description: `Order ${message.orderUUID}: ${message.warehouse.name} — ${message.error ?? 'unknown error'}`,
                },
          });
        }
      },
    );

    this.socket.on(
      'carrier.create-packet.response',
      (message: ICarrierCreatePacketResponseMessage): void => {
        this.showNotification({
          message: message.success
            ? {
                severity: ENotificationSeverity.SUCCESS,
                title: 'Packet created',
                description: `Order ${message.order.uuid}: packet ${message.carrierPacket?.barcode ?? ''} was created.`,
              }
            : {
                severity: ENotificationSeverity.ERROR,
                title: 'Packet creation failed',
                description: `Order ${message.order.uuid}: ${message.error ?? 'unknown error'}`,
              },
        });
      },
    );
  }

  public subscribeEventMessenger(): void {
    this.socket.on('connect', this.joinMessengerRooms);
    this.joinMessengerRooms();

    this.socket.on(
      EMessengerSocketEvent.MESSAGE,
      (message: { payload: IMessenger }): void => {
        this.store.dispatch({
          type: customerSendMessage.type,
          ...message,
        });
      },
    );

    this.socket.on(
      EMessengerSocketEvent.LEAD_SESSIONS,
      (message: ILeadSessions): void => {
        this.store.dispatch({
          type: leadSessions.type,
          ...message,
        });
      },
    );

    this.socket.on(
      EMessengerSocketEvent.LEAD_BASKET_PRODUCTS,
      (message: ILeadBasketProducts): void => {
        this.store.dispatch({
          type: leadBasketProducts.type,
          ...message,
        });
      },
    );

    this.socket.on(
      EMessengerSocketEvent.LEAD_ACTIVITY,
      (message: ILeadActivity): void => {
        this.store.dispatch({
          type: leadActivity.type,
          ...message,
        });
      },
    );

    this.socket.on(
      EMessengerSocketEvent.EVENT,
      (message: { payload: IAdminEvent }): void => {
        if (message.payload.event.code === EEventCode.TICK) {
          this.store.dispatch({
            type: customerTick.type,
            ...message,
          });
        }
      },
    );
  }

  public unsubscribeEventMessenger(): void {
    this.socket.off('connect', this.joinMessengerRooms);
    this.socket.off(EMessengerSocketEvent.MESSAGE);
    this.socket.off(EMessengerSocketEvent.LEAD_SESSIONS);
    this.socket.off(EMessengerSocketEvent.LEAD_BASKET_PRODUCTS);
    this.socket.off(EMessengerSocketEvent.LEAD_ACTIVITY);
    this.socket.off(EMessengerSocketEvent.EVENT);

    if (this.socket.connected) {
      this.socket.emit('leave', { room: ESocketRoom.MESSENGER });

      const activeLeadUUID: string | undefined =
        this.store.getState().Messenger.lead.uuid;

      if (activeLeadUUID) {
        this.socket.emit('leave', {
          room: getMessengerLeadRoom(activeLeadUUID),
        });
      }
    }
  }

  private readonly joinMessengerRooms = (): void => {
    if (!this.socket.connected) {
      return;
    }

    this.socket.emit('join', { room: ESocketRoom.MESSENGER });

    const activeLeadUUID: string | undefined =
      this.store.getState().Messenger.lead.uuid;

    if (activeLeadUUID) {
      this.socket.emit('join', { room: getMessengerLeadRoom(activeLeadUUID) });
    }
  };

  public subscribeEventShowNotification(cb: ShowNotificationCallback): void {
    this.socket.on(
      ESocketActions.SHOW_NOTIFICATION,
      (message: ShowNotificationMessage): void => cb(message),
    );
  }

  public unsubscribeEventShowNotification(): void {
    this.socket.off(ESocketActions.SHOW_NOTIFICATION);
  }

  public joinProductRoom({ productUUID }: { productUUID: string }): () => void {
    if (
      !productUUID ||
      productUUID === 'undefined' ||
      productUUID === 'null' ||
      !this.socket.connected
    ) {
      return (): void => {};
    }

    const room: string = `product/${productUUID}`;

    if (this.joinedRooms.has(room)) {
      return (): void => {};
    }

    this.joinedRooms.add(room);
    this.socket.emit('join', { room });

    this.socket.on(
      'product.event',
      (message: { payload: IProductEvent }): void =>
        eventProduct({
          message: message.payload,
          showNotification: this.showNotification.bind(this),
        }),
    );

    return (): void => {
      if (this.socket.connected) {
        this.socket.emit('leave', { room });
        this.socket.off('product.event');

        this.joinedRooms.delete(room);
      }
    };
  }

  public joinWarehouseProductRoom({
    warehouseProduct,
  }: {
    warehouseProduct?: IAdminWarehouseProductDetail | null;
  }): () => void {
    if (!this.socket.connected || !warehouseProduct) {
      return (): void => {};
    }
    const room: string = `warehouse-product/${warehouseProduct.uuid}`;

    if (this.joinedRooms.has(room)) {
      return (): void => {};
    }

    this.joinedRooms.add(room);
    this.socket.emit('join', { room });

    this.socket.on(
      'warehouse-product.event',
      (message: { payload: IProductEvent }): void =>
        eventProduct({
          message: message.payload,
          showNotification: this.showNotification.bind(this),
        }),
    );

    return (): void => {
      if (this.socket.connected) {
        const room: string = `warehouse-product/${warehouseProduct.uuid}`;

        this.socket.emit('leave', { room });
        this.socket.off('warehouse-product.event');

        this.joinedRooms.delete(room);
      }
    };
  }

  private showNotification({
    message,
  }: {
    message: ShowNotificationMessage;
  }): void {
    if (!this.openNotification) {
      return;
    }

    this.openNotification('topRight', message);
  }
}
